import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useUser } from "@clerk/clerk-react";
import { setApiOwnerId } from "../services/api";

export interface OwnerInfo {
  id: string;
  name: string;
  email: string;
}

interface OwnerContextType {
  owner: OwnerInfo | null;
  isLoading: boolean;
}

const OwnerContext = createContext<OwnerContextType | undefined>(undefined);

interface OwnerProviderProps {
  children: ReactNode;
}

export const OwnerProvider: React.FC<OwnerProviderProps> = ({ children }) => {
  const { user, isLoaded } = useUser();
  const [owner, setOwner] = useState<OwnerInfo | null>(null);

  useEffect(() => {
    if (!isLoaded || !user) {
      setOwner(null);
      setApiOwnerId(null);
      return;
    }

    const ownerId = (user.publicMetadata?.ownerId as string) || user.id || "";
    const ownerName =
      (user.publicMetadata?.ownerName as string) ||
      user.organizationMemberships?.[0]?.organization?.name ||
      user.fullName ||
      "";
    const ownerEmail = user.primaryEmailAddress?.emailAddress || "";

    setOwner({
      id: ownerId,
      name: ownerName,
      email: ownerEmail,
    });
    setApiOwnerId(ownerId);
  }, [isLoaded, user]);

  return (
    <OwnerContext.Provider value={{ owner, isLoading: !isLoaded }}>
      {children}
    </OwnerContext.Provider>
  );
};

export const useOwner = (): OwnerContextType => {
  const context = useContext(OwnerContext);
  if (context === undefined) {
    throw new Error("useOwner must be used within an OwnerProvider");
  }
  return context;
};
